import ListActionsMenu from "../documents/ListActionsMenu";
import SifenReportModal from "../sifen-reports/SifenReportModal";
import {
  CButton,
  CButtonGroup,
  CDropdown,
  CDropdownItem,
  CDropdownToggle,
  CTooltip,
} from "@coreui/react";
import {
  DEBIT_NOTE_STATUS_APPROVED_BY_SET,
  DEBIT_NOTE_STATUS_CANCELLATION_FAILED,
  // DEBIT_NOTE_STATUS_CANCELLATION_FAILED,
  DEBIT_NOTE_STATUS_ERROR,
  DEBIT_NOTE_STATUS_PENDING,
  DEBIT_NOTE_STATUS_RECOVERABLE_ERROR,
  DEBIT_NOTE_STATUS_SET_UNKNOWN_ERROR,
  DEBIT_NOTE_STATUS_UPLOADED_TO_SET,
  //DEBIT_NOTE_STATUS_UPLOADED_TO_SET,
  DebitNote,
} from "../../models/debit-note";
import React, { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import {
  DEBIT_NOTES_DESTROY,
  DEBIT_NOTES_RETRIEVE,
  DEBIT_NOTES_UPDATE,
} from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";

const MAX_VISIBLE_ACTIONS = 3;

interface DebitNoteCrudAtionsProps {
  debitNote: DebitNote;
  onCancellationClick: (_: DebitNote) => void;
  onCancellationRetryClick: (_: DebitNote) => void;
  onCancellationRevertClick: (_: DebitNote) => void;
  onUpdateClick: (_: DebitNote) => void;
  onDeleteClick: (_: DebitNote) => void;
  onConsultByCdcClick: (_: DebitNote) => void;
  onResendClick: (_: DebitNote) => void;
  onSendEmailClick: (_: DebitNote) => void;
  onHistoryClick: (_: DebitNote) => void;
}

const DebitNoteCrudActions: React.FC<DebitNoteCrudAtionsProps> = ({
  debitNote,
  onCancellationClick,
  onCancellationRetryClick,
  onCancellationRevertClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onResendClick,
  onSendEmailClick,
  onHistoryClick,
}) => {
  const [showSifenReport, setShowSifenReport] = useState(false);
  const [currentDebitNote, setCurrentDebitNote] = useState(debitNote);

  useEffect(() => {
    setCurrentDebitNote(debitNote);
  }, [debitNote]);

  useSub(`debit_note_${debitNote.id}`, (debitNote: DebitNote) => {
    setCurrentDebitNote(debitNote);
  });
  const cancelButton = (
    <PermissionRequiredComponent permissionName={DEBIT_NOTES_UPDATE}>
      <CTooltip content="Anular">
        <CButton
          className="text-white"
          color="secondary"
          onClick={() => {
            onCancellationClick(debitNote);
          }}
        >
          <i className="fa fa-ban"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const editButton = (
    <PermissionRequiredComponent permissionName={DEBIT_NOTES_UPDATE}>
      <CTooltip content="Editar">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onUpdateClick(debitNote);
          }}
        >
          <i className="fa fa-pencil"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const deleteButton = (
    <PermissionRequiredComponent permissionName={DEBIT_NOTES_DESTROY}>
      <CTooltip content="Eliminar">
        <CButton
          className="text-white"
          color="danger"
          onClick={() => {
            onDeleteClick(debitNote);
          }}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const documentDateTime = new Date(currentDebitNote.createdAt!).getTime();
  const now = new Date().getTime();

  const olderThan7Days = documentDateTime < now - 7 * 24 * 60 * 60 * 1000;

  const shouldShowCancelButton =
    currentDebitNote.status === DEBIT_NOTE_STATUS_APPROVED_BY_SET &&
    !olderThan7Days;

  const shouldShowEditAndDeleteButton =
    (currentDebitNote.status === DEBIT_NOTE_STATUS_PENDING ||
      currentDebitNote.status === DEBIT_NOTE_STATUS_ERROR ||
      currentDebitNote.status === DEBIT_NOTE_STATUS_SET_UNKNOWN_ERROR ||
      currentDebitNote.setResponseCode === "1001" ||
      currentDebitNote.setResponseCode === "4003") &&
    currentDebitNote.status !== DEBIT_NOTE_STATUS_RECOVERABLE_ERROR;

  const consultByCdcButton = (
    <PermissionRequiredComponent permissionName={DEBIT_NOTES_UPDATE}>
      <CTooltip content="Consultar por CDC">
        <CButton
          className="text-white"
          color="primary"
          onClick={() => {
            onConsultByCdcClick(debitNote);
          }}
        >
          <i className="fa fa-question-circle"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowConsultByCdcButton =
    currentDebitNote.status === DEBIT_NOTE_STATUS_UPLOADED_TO_SET ||
    currentDebitNote.status === DEBIT_NOTE_STATUS_ERROR ||
    currentDebitNote.setResponseCode === "1001" ||
    currentDebitNote.setResponseCode === "4003";

  const resendButton = (
    <PermissionRequiredComponent permissionName={DEBIT_NOTES_UPDATE}>
      <CTooltip content="Reenviar">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onResendClick(debitNote);
          }}
        >
          <i className="fa fa-refresh"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowResendButton =
    currentDebitNote.status === DEBIT_NOTE_STATUS_ERROR;

  const sendEmailButton = (
    <PermissionRequiredComponent permissionName={DEBIT_NOTES_UPDATE}>
      <CTooltip
        content={
          currentDebitNote.emailSent ? "Reenviar Correo" : "Enviar Correo"
        }
      >
        <CButton
          className="text-white"
          color="info"
          onClick={() => {
            onSendEmailClick(debitNote);
          }}
        >
          <i className="fa fa-envelope"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowSendEmailButton =
    currentDebitNote.status === DEBIT_NOTE_STATUS_APPROVED_BY_SET;

  const retryCancellationButton = (
    <PermissionRequiredComponent permissionName={DEBIT_NOTES_UPDATE}>
      <CTooltip content="Reintentar Anulación">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onCancellationRetryClick(currentDebitNote);
          }}
        >
          <i className="fa fa-rotate-right"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRetryCancellationButton =
    debitNote.status === DEBIT_NOTE_STATUS_CANCELLATION_FAILED;

  const revertCancellationButton = (
    <PermissionRequiredComponent permissionName={DEBIT_NOTES_UPDATE}>
      <CTooltip content="Revertir Anulación">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onCancellationRevertClick(currentDebitNote);
          }}
        >
          <i className="fa fa-backward"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRevertCancellationButton =
    debitNote.status === DEBIT_NOTE_STATUS_CANCELLATION_FAILED;

  const historyButton = (
    <PermissionRequiredComponent permissionName={DEBIT_NOTES_RETRIEVE}>
      <CTooltip content="Ver Historial">
        <CButton
          className="text-white"
          color="dark"
          onClick={() => {
            onHistoryClick(currentDebitNote);
          }}
        >
          <i className="fa fa-history"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const allActions = [
    {
      element: historyButton,
      shouldShow: true,
      label: "Ver Historial",
      icon: "fa-history",
      permission: DEBIT_NOTES_RETRIEVE,
      onClick: () => onHistoryClick(currentDebitNote),
    },
    {
      element: cancelButton,
      shouldShow: shouldShowCancelButton,
      label: "Anular",
      icon: "fa-ban",
      permission: DEBIT_NOTES_UPDATE,
      onClick: () => onCancellationClick(debitNote),
    },
    {
      element: editButton,
      shouldShow: shouldShowEditAndDeleteButton,
      label: "Editar",
      icon: "fa-pencil",
      permission: DEBIT_NOTES_UPDATE,
      onClick: () => onUpdateClick(debitNote),
    },
    {
      element: deleteButton,
      shouldShow: shouldShowEditAndDeleteButton,
      label: "Eliminar",
      icon: "fa-trash",
      permission: DEBIT_NOTES_DESTROY,
      onClick: () => onDeleteClick(debitNote),
    },
    {
      element: consultByCdcButton,
      shouldShow: shouldShowConsultByCdcButton,
      label: "Consultar por CDC",
      icon: "fa-question-circle",
      permission: DEBIT_NOTES_UPDATE,
      onClick: () => onConsultByCdcClick(debitNote),
    },
    {
      element: resendButton,
      shouldShow: shouldShowResendButton,
      label: "Reenviar",
      icon: "fa-refresh",
      permission: DEBIT_NOTES_UPDATE,
      onClick: () => onResendClick(debitNote),
    },
    {
      element: retryCancellationButton,
      shouldShow: shouldShowRetryCancellationButton,
      label: "Reintentar Anulación",
      icon: "fa-rotate-right",
      permission: DEBIT_NOTES_UPDATE,
      onClick: () => onCancellationRetryClick(currentDebitNote),
    },
    {
      element: revertCancellationButton,
      shouldShow: shouldShowRevertCancellationButton,
      label: "Revertir Anulación",
      icon: "fa-backward",
      permission: DEBIT_NOTES_UPDATE,
      onClick: () => onCancellationRevertClick(currentDebitNote),
    },
    {
      element: sendEmailButton,
      shouldShow: shouldShowSendEmailButton,
      label: currentDebitNote.emailSent ? "Reenviar Correo" : "Enviar Correo",
      icon: "fa-envelope",
      permission: DEBIT_NOTES_UPDATE,
      onClick: () => onSendEmailClick(debitNote),
    },
    {
      element: (
        <PermissionRequiredComponent permissionName={DEBIT_NOTES_UPDATE}>
          <CTooltip content="Preparar reporte para DNIT">
            <CButton color="warning" aria-label="Preparar reporte para DNIT" onClick={() => setShowSifenReport(true)}>
              <i className="fa fa-flag"></i>
            </CButton>
          </CTooltip>
        </PermissionRequiredComponent>
      ),
      shouldShow: currentDebitNote.status === "cancelled",
      label: "Preparar reporte para DNIT",
      icon: "fa-flag",
      permission: DEBIT_NOTES_UPDATE,
      onClick: () => setShowSifenReport(true),
    },
  ];

  const visibleActions = allActions.filter((a) => a.shouldShow);
  const shouldCollapse = visibleActions.length > MAX_VISIBLE_ACTIONS;
  const primaryActions = shouldCollapse
    ? visibleActions.slice(0, MAX_VISIBLE_ACTIONS - 1)
    : visibleActions;
  const overflowActions = shouldCollapse
    ? visibleActions.slice(MAX_VISIBLE_ACTIONS - 1)
    : [];

  return (
    <td className="text-center">
      <CButtonGroup>
        {primaryActions.map((a, i) => (
          <React.Fragment key={i}>{a.element}</React.Fragment>
        ))}
        {shouldCollapse && (
          <CDropdown className="btn-group">
            <CTooltip content="Más opciones">
              <CDropdownToggle
                color="primary"
                className="text-white"
                caret={false}
                style={{ borderTopRightRadius: "0.25rem", borderBottomRightRadius: "0.25rem" }}
              >
                <i className="fa fa-ellipsis-v"></i>
              </CDropdownToggle>
            </CTooltip>
            <ListActionsMenu>
              {overflowActions.map((a, i) => (
                <PermissionRequiredComponent
                  key={i}
                  permissionName={a.permission}
                >
                  <CDropdownItem onClick={a.onClick}>
                    <i className={`fa ${a.icon} mr-2`}></i>
                    {a.label}
                  </CDropdownItem>
                </PermissionRequiredComponent>
              ))}
            </ListActionsMenu>
          </CDropdown>
        )}
      </CButtonGroup>
      {showSifenReport && <SifenReportModal
        show={showSifenReport}
        documentType="electronic_debit_note"
        documentId={currentDebitNote.id}
        onClose={() => setShowSifenReport(false)}
      />}
    </td>
  );
};

export default DebitNoteCrudActions;
